/**
 * Sprint M5.23 (24.07.2026) — Dedicated Landing-Page fuer Meta-Ads-Traffic.
 *
 * Route: /lp/flex4success
 *
 * Strategie:
 *  - Cold-Traffic aus Meta-Ads (Problem-Awareness Stage 2)
 *  - KEINE Framework-Begriffe, KEIN Social Proof (V1)
 *  - KEIN Cookie-Banner, KEIN Tracker-Load auf dieser Seite
 *    (Consent + Analytics kommen erst NACH Magic-Link im Cockpit)
 *  - Pain-driven Copy, Message-Match zur Ad
 *  - Backend: /api/warteliste/opt-in mit source="lp_flex4success"
 *
 * Rechtlicher Rahmen (Andreas-Freigabe pending, Anwalt-Check):
 *  Da auf dieser LP KEINE Tracker/Cookies (ausser essentials fuer POST)
 *  geladen werden, ist ein Consent-Banner nicht gesetzlich erforderlich.
 *  Dezenter Text-Hinweis mit Datenschutz-Link unter dem Formular reicht.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Mail, MousePointerClick, Rocket, Clock, ClipboardCheck, PlayCircle, Sparkles } from "lucide-react";
// Sprint M5.32 (27.07.2026) — Provider-Box mit In-App-Reconfirm-Fallback.
import ProviderConfirmBox from "@/components/ProviderConfirmBox";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCENT = "#FF6B35";

export default function LPFlex4Success() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Sprint V1.H (30.08.2026) — Preview-only Test-Login-Panel.
  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const eyebrowClickCountRef = useRef(0);
  const eyebrowClickTimerRef = useRef(null);
  // Sprint M5.32-Hotfix-Fix-2 (28.07.2026) — Change-Email-Mode.
  // Bei ?change_email=true kommt der User aus dem iCloud-Warnblock der Danke-
  // Seite zurueck, um eine andere Adresse zu benutzen. Wir fokussieren das
  // Feld, blenden einen Hinweis ein und scrollen sanft zum Formular.
  const changeEmailMode = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("change_email") === "true";
    } catch {
      return false;
    }
  }, []);
  const emailInputRef = useRef(null);

  // Sprint V1.H (30.08.2026) — Preview-only Test-Login.
  // (a) ?test_login=<tier> → sofort redirect zum Backend-Endpoint.
  // (b) 5x-Klick auf Subline → Debug-Panel oeffnet (nur wenn Preview).
  useEffect(() => {
    let cancelled = false;
    // Check ?test_login=<tier>
    try {
      const p = new URLSearchParams(window.location.search);
      const tier = p.get("test_login");
      if (tier) {
        // Server-Endpoint prueft ENV != production — Prod liefert 404,
        // dann bleibt der User einfach auf /start (kein Redirect).
        window.location.href = `${API}/api/preview/test-login?tier=${encodeURIComponent(tier)}`;
        return;
      }
    } catch {
      // ignore
    }
    // Debug-Panel-Gate: nur wenn Backend Preview meldet.
    axios
      .get(`${API}/api/preview/enabled`, { withCredentials: false })
      .then((r) => {
        if (!cancelled) setPreviewEnabled(Boolean(r.data?.enabled));
      })
      .catch(() => {
        if (!cancelled) setPreviewEnabled(false);
      });
    return () => {
      cancelled = true;
      if (eyebrowClickTimerRef.current) clearTimeout(eyebrowClickTimerRef.current);
    };
  }, []);

  function handleEyebrowClick() {
    eyebrowClickCountRef.current += 1;
    if (eyebrowClickTimerRef.current) clearTimeout(eyebrowClickTimerRef.current);
    // Reset counter nach 1500ms Inaktivitaet
    eyebrowClickTimerRef.current = setTimeout(() => {
      eyebrowClickCountRef.current = 0;
    }, 1500);
    if (eyebrowClickCountRef.current >= 5 && previewEnabled) {
      setDebugOpen(true);
      eyebrowClickCountRef.current = 0;
    }
  }

  useEffect(() => {
    if (!changeEmailMode) return;
    // Nach Mount: Feld leeren (falls Browser autofillt), Fokus + Scroll.
    setEmail("");
    if (emailInputRef.current) {
      emailInputRef.current.focus({ preventScroll: true });
      emailInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [changeEmailMode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Bitte gib eine gueltige E-Mail-Adresse ein.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/warteliste/opt-in`,
        {
          email: trimmed,
          first_name: firstName.trim() || null,
          consent: true,
          source: "lp_flex4success",
        },
        { withCredentials: true }
      );
      // Direkt auf Danke-Seite (LP-spezifisch) — Sprint M5.32: `?email=`
      // wird mitgegeben, damit die Provider-Box dort erkennen kann, welcher
      // Mail-Anbieter zu welchem Postfach-Tab hinweist.
      navigate(`/lp/flex4success/danke?email=${encodeURIComponent(trimmed)}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Etwas ist schiefgelaufen. Bitte in einer Minute erneut versuchen.";
      setError(typeof detail === "string" ? detail : "Bitte in einer Minute erneut versuchen.");
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="lp-flex4success-page"
    >
      <main className="max-w-5xl mx-auto px-5 sm:px-6 pt-8 sm:pt-14 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10 items-center">
          {/* LINKS 60% — Copy + Form */}
          <div className="md:col-span-3">
            {/* Sprint V11 (Boardroom-CoS Sa 05.09.2026-17 Uhr) — "Wir sind live"-
                Badge auf der Charmeseite. Sichtbar auch im Prelaunch-Modus, damit
                User den Live-Status sofort erkennen. */}
            <p
              data-testid="lp-live-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] sm:text-xs font-bold uppercase tracking-[0.24em] mb-4"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Wir sind live
            </p>

            {/* Subline */}
            <p
              className="text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] cursor-default select-none"
              style={{ color: ACCENT }}
              data-testid="lp-subline"
              onClick={handleEyebrowClick}
              title={previewEnabled ? "Preview-Modus (5x tap für Test-Login-Panel)" : undefined}
            >
              Du redest mit jemandem.
            </p>

            {/* Headline — 12px Abstand auf Mobile (Sprint M5.24 Hotfix: Singular-Match zur Subline) */}
            <h1
              className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.15] tracking-tight"
              data-testid="lp-headline"
            >
              Ein Gespräch läuft plötzlich aus dem Ruder — und du willst verstehen, warum.
            </h1>

            {/* Pain-Body — 16px auf Mobile (M5.24.2: Brueckenzeile wieder aufgenommen) */}
            <div
              className="mt-4 sm:mt-6 space-y-2 text-base sm:text-lg text-slate-700 leading-relaxed"
              data-testid="lp-pain-body"
            >
              <p>Es gibt typische Reaktionsmuster.</p>
              <p>Wenn du erkennst, welches deines ist,</p>
              <p>verstehst du, warum Gespräche immer wieder kippen.</p>
              <p>…und welche Flexpfade es gibt, um das zu lösen.</p>
            </div>

            {/* Stufe 3 (Sprint v1.5, 02.09.2026) — Startseiten-Erklärung nach
                Metaphern-Hierarchie v1.4: Flexpfad = persönlicher Entwicklungsweg. */}
            <p
              className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed italic"
              data-testid="lp-flexpfad-erklaerung"
            >
              Der Flexpfad ist dein persönlicher Entwicklungsweg zu bewusster Wirkung.
            </p>

            {/* Bridge — Sprint M5.24.2: handlungs-orientierte Mini-Headline mit CTA-Anker */}
            <p
              className="mt-6 sm:mt-8 text-lg sm:text-xl font-bold text-slate-900 leading-snug"
              data-testid="lp-bridge"
            >
              Wenn du deine Muster erkennen willst, dann fordere jetzt deinen Magic-Link an, um es in zwei Minuten herauszufinden.
            </p>

            {/* Form — Sprint M5.24.1: enger an die fette Bridge-Headline gerueckt */}
            <form
              onSubmit={handleSubmit}
              className="mt-3 space-y-2.5"
              data-testid="lp-form"
            >
              {/* Sprint M5.32-Hotfix-Fix-2 — Hinweis nach Redirect aus iCloud-Warnblock */}
              {changeEmailMode && (
                <div
                  className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="lp-change-email-banner"
                >
                  Kein Problem. Trag hier eine andere E-Mail-Adresse ein (z. B. Gmail oder GMX) —
                  damit landet dein Zugang zuverlässig bei dir.
                </div>
              )}
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Dein Vorname (optional)"
                className="w-full px-4 py-3.5 rounded-xl border border-slate-300 text-base placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-1 transition"
                style={{ "--tw-ring-color": ACCENT }}
                data-testid="lp-first-name-input"
                disabled={busy}
                autoComplete="given-name"
              />
              <input
                ref={emailInputRef}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@email.de"
                className="w-full px-4 py-3.5 rounded-xl border border-slate-300 text-base placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-1 transition"
                style={{ "--tw-ring-color": ACCENT }}
                data-testid="lp-email-input"
                disabled={busy}
                autoComplete="email"
                inputMode="email"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-semibold text-base shadow-md transition disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ backgroundColor: ACCENT }}
                data-testid="lp-submit-button"
              >
                {busy ? "Sende Magic-Link..." : (
                  <>
                    Mein Muster erkennen
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {error && (
                <p
                  className="text-sm text-red-600 mt-2"
                  role="alert"
                  data-testid="lp-error"
                >
                  {error}
                </p>
              )}
            </form>

            {/* Microcopy — Sprint M5.24.2: eine Zeile, Zustellungs-Kanal + Sicherheits-Reassurance */}
            <div
              className="mt-2 text-xs text-slate-500 leading-relaxed space-y-1"
              data-testid="lp-microcopy"
            >
              <p>Du erhältst den Magic-Link direkt per E-Mail. Deine Daten sind bei uns sicher.</p>
              <p className="pt-1">
                Details in unserer{" "}
                <a
                  href="/datenschutz"
                  className="underline underline-offset-2 hover:text-slate-700"
                  data-testid="lp-privacy-link"
                >
                  Datenschutzerklärung
                </a>
                .
              </p>
            </div>
          </div>

          {/* RECHTS 40% — 4-Fokustypen-Visual (Desktop only, mobile hidden) */}
          <aside
            className="hidden md:flex md:col-span-2 flex-col items-center justify-center"
            data-testid="lp-visual-grid"
          >
            <div className="grid grid-cols-2 gap-4 w-full max-w-[280px]" aria-hidden="true">
              {/* Sprint M5.49 Hotfix (02.08.2026) — Matrix: Katze OL, Eule OR, Hund UL, Biene UR. */}
              {["katze", "eule", "hund", "biene"].map((slug) => (
                <div
                  key={slug}
                  className="aspect-square rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center p-4 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`lp-visual-tile-${slug}`}
                >
                  <img
                    src={`/icons/icon-${slug}.png`}
                    alt=""
                    className="w-full h-full object-contain"
                    draggable="false"
                  />
                </div>
              ))}
            </div>
            {/* Sprint M5.24 Hotfix: mystische Caption unter dem 4-Tier-Visual */}
            <p
              className="mt-5 max-w-[280px] text-center text-sm italic text-slate-500 leading-snug"
              data-testid="lp-visual-caption"
            >
              Warum du mit den Reaktionsmustern dieser vier mehr gemeinsam hast, als du ahnst.
            </p>
          </aside>
        </div>
      </main>

      {/* Sprint V1.H (30.08.2026) — Preview-only Test-Login-Debug-Panel.
          Sichtbar nur wenn: previewEnabled=true UND 5x Klick auf Subline.
          Fixed-Bottom, dismissible. NIE in Prod (Backend-Gate: APP_ENV != production). */}
      {previewEnabled && debugOpen && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 border-t-4 border-amber-500 bg-slate-900 text-white shadow-2xl"
          data-testid="v1h-debug-panel"
        >
          <div className="max-w-5xl mx-auto px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400">
                  Preview · V1.H Test-Login
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  Ein-Klick-Login als eine der 7 Test-Rollen. Nur im Preview sichtbar.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDebugOpen(false)}
                className="text-slate-400 hover:text-white text-2xl leading-none px-2"
                aria-label="Schließen"
                data-testid="v1h-debug-close"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {[
                { tier: "free",       label: "Free",        color: "bg-slate-600 hover:bg-slate-500" },
                { tier: "pro-month",  label: "Pro Monat",   color: "bg-blue-600 hover:bg-blue-500" },
                { tier: "pro-year",   label: "Pro Jahr",    color: "bg-indigo-600 hover:bg-indigo-500" },
                { tier: "powerpaket", label: "PowerPaket",  color: "bg-purple-600 hover:bg-purple-500" },
                { tier: "cancelled",  label: "Cancelled",   color: "bg-orange-600 hover:bg-orange-500" },
                { tier: "expired",    label: "Expired",     color: "bg-red-700 hover:bg-red-600" },
                { tier: "pionier",    label: "Pionier",     color: "bg-amber-600 hover:bg-amber-500" },
              ].map((btn) => (
                <a
                  key={btn.tier}
                  href={`${API}/api/preview/test-login?tier=${btn.tier}`}
                  className={`${btn.color} text-white text-xs sm:text-sm font-bold rounded-lg px-3 py-2.5 text-center transition-colors`}
                  data-testid={`v1h-debug-btn-${btn.tier}`}
                >
                  {btn.label}
                </a>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
              Alternativ per URL: <code className="text-slate-300">/start?test_login=&lt;tier&gt;</code>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sprint M5.26 (25.07.2026) — Confirm-Seite Conversion-Bridge.
 *
 * 4 Sektionen halten die Aufmerksamkeit zwischen Opt-in und Mail-Öffnung:
 *   1. HERO         — Zeit-Anker + pulsierendes Mail-Symbol + Watermark-Icons
 *   2. VALUE-BLOCK  — 3 Outcome-Bullets mit Fokustyp-Icons
 *   3. STEPS        — 3 nummerierte Schritte (Desktop horizontal, Mobile vertikal)
 *   4. FOOTER       — Zeit-Anker, Helper-Links, Vertrauens-Zeile
 */
export function LPFlex4SuccessDanke() {
  const [countdown, setCountdown] = useState(60);
  // Sprint M5.32 (27.07.2026) — Email aus URL für Provider-Box-Hint.
  const emailFromUrl = React.useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const e = (p.get("email") || "").trim();
      return e || "";
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const timeLabel = countdown > 0 ? `${countdown} Sekunden` : "einem Moment";

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="lp-flex4success-danke-page"
    >
      <main className="max-w-3xl mx-auto px-5 sm:px-6 pt-10 sm:pt-14 pb-16">
        {/* ============ 1. HERO ============ */}
        <section className="relative text-center" data-testid="danke-hero">
          {/* Watermark: 4 Fokustyp-Icons als Wiedererkennungs-Anker zur LP */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-start justify-center pt-8 opacity-[0.07]"
            data-testid="danke-watermark"
          >
            <div className="grid grid-cols-4 gap-4 sm:gap-8 max-w-md">
              {["katze", "eule", "biene", "hund"].map((slug) => (
                <img
                  key={slug}
                  src={`/icons/icon-${slug}.png`}
                  alt=""
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                  draggable="false"
                />
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Pulsierendes Mail-Symbol als visueller Anker */}
            <div className="mx-auto w-16 h-16 mb-6 relative">
              <span
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: `${ACCENT}33` }}
                aria-hidden="true"
              />
              <div
                className="relative w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${ACCENT}22`, color: ACCENT }}
              >
                <Mail className="w-8 h-8" />
              </div>
            </div>

            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1]"
              data-testid="danke-headline"
            >
              Fast geschafft. Deine Mail ist unterwegs.
            </h1>
            <p
              className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl mx-auto"
              data-testid="danke-subline"
            >
              In {timeLabel} bekommst du Zugang zu deinem Fokustyp und den passenden Reaktionsmustern.
            </p>
          </div>
        </section>

        {/* Sprint M5.32 (27.07.2026) — Provider-Box direkt nach Hero,
            damit der User sofort sieht, wo seine Bestätigungs-Mail liegt.
            Rendert nur, wenn `?email=` aus dem Opt-in-Redirect mitkam. */}
        {emailFromUrl && (
          <section className="mt-10 sm:mt-12" data-testid="danke-provider-slot">
            <ProviderConfirmBox email={emailFromUrl} />
          </section>
        )}

        {/* ============ 2. VALUE-BLOCK ============ */}
        <section className="mt-14 sm:mt-16" data-testid="danke-value-block">
          <h2 className="text-center text-xl sm:text-2xl font-bold text-slate-900">
            Was du gleich im System findest
          </h2>
          <ul className="mt-6 space-y-3 max-w-lg mx-auto">
            {[
              {
                Icon: ClipboardCheck,
                text: "Dein Reaktionsmuster – in 5 Minuten sichtbar",
              },
              {
                Icon: PlayCircle,
                text: "Video-Einführung in die Reaktionsmuster der vier Fokustypen",
              },
              {
                Icon: Sparkles,
                text: "Erste Hinweise zur Gestaltung deiner Kommunikation",
              },
            ].map((item, i) => (
              <li
                key={item.text}
                className="flex items-center gap-4 p-3 sm:p-4 rounded-xl bg-slate-50 border border-slate-100 animate-in fade-in-0 slide-in-from-bottom-2 duration-700"
                style={{ animationDelay: `${150 + i * 140}ms`, animationFillMode: "backwards" }}
                data-testid={`danke-value-${i + 1}`}
              >
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}18`, color: ACCENT }}
                  aria-hidden="true"
                >
                  <item.Icon className="w-5 h-5" />
                </div>
                <span className="text-sm sm:text-base font-medium text-slate-800 leading-snug">
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ============ 3. STEPS ============ */}
        <section className="mt-14 sm:mt-16" data-testid="danke-steps">
          <h2 className="text-center text-xl sm:text-2xl font-bold text-slate-900">
            Was du jetzt tust
          </h2>
          <ol className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                Icon: Mail,
                title: "Öffne dein E-Mail-Programm",
                desc: "Handy: Mail-App. Desktop: Gmail, Outlook oder Web-Client.",
              },
              {
                Icon: MousePointerClick,
                title: "Klick auf den Button in der Mail",
                desc: '\u201EJa, ich bin drin\u201C \u2014 ein Klick reicht.',
              },
              {
                Icon: Rocket,
                title: "Du bist drin — leg direkt los",
                desc: "Kein Passwort. Kein Zweit-Login. Direkt in deinen PocketCoach.",
              },
            ].map((step, i) => (
              <li
                key={step.title}
                className="relative p-5 pt-7 rounded-xl bg-white border border-slate-200 shadow-sm text-center"
                data-testid={`danke-step-${i + 1}`}
              >
                <div
                  className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
                  style={{ backgroundColor: ACCENT }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <step.Icon className="w-7 h-7 mx-auto mb-3" style={{ color: ACCENT }} />
                <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-snug">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {step.desc}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* ============ 4. FOOTER (Zeit-Anker + Helper + Vertrauen) ============ */}
        <div className="mt-12 text-center" data-testid="danke-time-anchor">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold"
            style={{ borderColor: `${ACCENT}55`, backgroundColor: `${ACCENT}0f`, color: ACCENT }}
          >
            <Clock className="w-4 h-4" />
            Du bist in 60 Sekunden drin.
          </div>
        </div>

        <div
          className="mt-8 text-center text-sm text-slate-500 space-y-2"
          data-testid="danke-hint"
        >
          <p>Nichts angekommen? Prüf auch deinen Spam-Ordner.</p>
          <p className="text-slate-500">
            {/* Sprint M5.38-Mini-Rest (28.07.2026) — „Nochmal senden" entfernt.
                Der frühere Link zeigte auf /api/auth/magic/reconfirm (jetzt 410).
                Re-Trigger laufen ausschliesslich ueber KlickTipp (M5.28d). */}
            <a
              href="/lp/flex4success?change_email=true"
              className="underline underline-offset-2 hover:text-slate-800"
              data-testid="danke-edit-email-link"
            >
              Andere E-Mail eintragen
            </a>
          </p>
        </div>

        <p
          className="mt-10 text-center text-xs text-slate-400"
          data-testid="danke-trust"
        >
          Jederzeit abbestellbar. Kein Spam. Deine Daten sind bei uns sicher.
        </p>
      </main>
    </div>
  );
}
